import { useState, useRef, useCallback, useEffect } from "react";
import { Loader } from "@/components/shared";
import SEOComponent from "@/components/shared/SEOComponent";
import { useOutletContext } from "react-router-dom";
import {
  Camera,
  Heart,
  Share,
  IndianRupee,
  Play,
  Volume2,
  VolumeX,
  X,
  Plus,
  AlertTriangle,
  CheckCircle,
  StopCircle,
  RotateCcw,
  Send,
  Bookmark,
  BookmarkCheck,
  Upload,
  Video,
  Info,
  GraduationCap,
  Store,
  TrendingUp,
  Laptop,
  User,
  Zap,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useUserContext } from "@/context/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Import the new bizz queries
import {
  useGetVideoFeed,
  useCreateVideoPost,
  useUploadVideoFile,
  useVideoInteractionManager,
  QUERY_KEYS,
} from "@/bizz/lib/react-query/bizzQueries";

import {
  generateThumbnail,
  getVideoMetadata,
} from "@/bizz/lib/appwrite/bizzApi";

import { uploadFile, getFileView, deleteFile } from "@/lib/appwrite/api";
import { useQueryClient } from "@tanstack/react-query";
import { useVideoTipPayment } from "@/bizz/lib/cashfree/videoTipPayment";

interface OutletContextType {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

interface VideoPost {
  $id: string;
  videoUrl: string;
  thumbnailUrl?: string;
  title: string;
  description: string;
  creator: {
    $id: string;
    name: string;
    username: string;
    imageUrl: string;
  };
  stats?: {
    likes: number;
    shares: number;
    tips: number;
    saves: number;
    views: number;
  };
  likes?: number;
  shares?: number;
  tips?: number;
  saves?: number;
  isLiked: boolean;
  isSaved: boolean;
  duration: number;
  createdAt: string;
  category: string;
}

// Video Post Validation Schema
const VideoPostValidation = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title too long"),
  description: z.string().max(500, "Description too long"),
  category: z.string().min(1, "Category is required"),
  file: z
    .array(z.instanceof(File))
    .min(1, "Video file is required")
    .max(1, "Only one video file allowed"),
});

const ENHANCED_BUSINESS_CATEGORIES = [
  // Keep your existing categories with enhanced descriptions
  {
    value: "business",
    label: "Business Showcase",
    description: "Promote your products, services, or local business",
    examples:
      "Bakery items, salon services, café specials, tailor work, electrician services",
    placeholder: "Show your products/services in action...",
    icon: Store,
    colorClass: "text-blue-500",
  },
  {
    value: "skills",
    label: "Skill Tutorials",
    description: "Teach valuable skills people can learn and apply",
    examples:
      "Cooking techniques, painting tutorials, makeup artistry, stitching methods, repair work",
    placeholder: "Share your expertise and teach others...",
    icon: GraduationCap,
    colorClass: "text-green-500",
  },
  {
    value: "marketing",
    label: "Marketing & Growth",
    description: "Marketing strategies and business growth tips",
    examples:
      "Social media marketing, customer acquisition, growth hacks, branding tips",
    placeholder: "Share your marketing wisdom and growth strategies...",
    icon: TrendingUp,
    colorClass: "text-purple-500",
  },
  {
    value: "finance",
    label: "Finance & Strategy",
    description: "Financial advice and business planning",
    examples:
      "Cost-saving tips, investment advice, pricing strategies, financial planning",
    placeholder: "Share financial insights and business strategies...",
    icon: IndianRupee,
    colorClass: "text-emerald-500",
  },
  {
    value: "technology",
    label: "Business Technology",
    description: "Tech tools and digital solutions for business",
    examples:
      "POS systems, billing software, social media tools, automation setup",
    placeholder: "Demonstrate tech tools that help businesses...",
    icon: Laptop,
    colorClass: "text-cyan-500",
  },
  {
    value: "entrepreneurship",
    label: "Entrepreneur Stories",
    description: "Real business journeys and startup experiences",
    examples:
      "Startup struggles, growth milestones, failure lessons, success stories",
    placeholder: "Tell your entrepreneurial story and experiences...",
    icon: User,
    colorClass: "text-orange-500",
  },

  // Add strategic new categories for future growth
  {
    value: "before-after",
    label: "Transformation Results",
    description: "Show before/after results of your services",
    examples:
      "Home cleaning, car detailing, beauty transformations, packaging design",
    placeholder: "Showcase your amazing transformations and results...",
    icon: Zap,
    colorClass: "text-yellow-500",
  },
  {
    value: "professional-training",
    label: "Professional Training",
    description: "Coaching, training, and consulting services",
    examples:
      "Sales training, language coaching, design mentoring, skill development",
    placeholder: "Demonstrate your training/coaching expertise...",
    icon: Award,
    colorClass: "text-pink-500",
  },
];

// 3. KEEP YOUR EXISTING CONTENT GUIDELINES (or enhance them)
const CONTENT_GUIDELINES = {
  allowed: [
    "Business tutorials and how-to content",
    "Product/service demonstrations and showcases",
    "Professional skill teaching and training",
    "Entrepreneurship advice and real stories",
    "Marketing strategies and growth tips",
    "Technology tools for business growth",
    "Financial advice and business planning",
    "Professional transformations and results",
  ],
  notAllowed: [
    "Entertainment-only content",
    "Dance/music videos (unless for events business)",
    "Personal vlogs unrelated to business",
    "Inappropriate or adult content",
    "Pure comedy/memes without business value",
    "Personal relationships/drama",
    "Non-educational lifestyle content",
    "Gaming content (unless business-related)",
  ],
};

const Bizz = () => {
  const { user } = useUserContext();
  const { selectedCategory } = useOutletContext<OutletContextType>();
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string>("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>("");
  const [videoDuration, setVideoDuration] = useState<number>(0);

  // Upload form state
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadCategory, setUploadCategory] = useState("business");

  // Tip modal state
  const [showTipModal, setShowTipModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoPost | null>(null);
  const [tipAmount, setTipAmount] = useState(10);
  const [tipMessage, setTipMessage] = useState("");

  const [videoInteractionStates, setVideoInteractionStates] = useState<{
    [videoId: string]: {
      isLiked: boolean;
      isSaved: boolean;
      tempLikes: number;
      tempSaves: number;
    };
  }>({});

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const recordingVideoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [uploadStage, setUploadStage] = useState<string>("");
  const queryClient = useQueryClient();

  const { handleTipPayment, isProcessing: isPaymentLoading } =
    useVideoTipPayment();
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);

  const getCategoryInfo = (categoryValue: string) => {
    return (
      ENHANCED_BUSINESS_CATEGORIES.find((cat) => cat.value === categoryValue) ||
      ENHANCED_BUSINESS_CATEGORIES[0]
    );
  };

  const getCategoryPlaceholder = (
    categoryValue: string,
    field: "title" | "description"
  ) => {
    const category = getCategoryInfo(categoryValue);
    if (field === "title") {
      return `${category.label}: Add a catchy title...`;
    }
    return category.placeholder;
  };

  // Form for hybrid upload approach
  const form = useForm<z.infer<typeof VideoPostValidation>>({
    resolver: zodResolver(VideoPostValidation),
    defaultValues: {
      title: "",
      description: "",
      category: "business",
      file: [],
    },
  });

  // Use the new bizz video feed query
  const {
    data: feedData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetVideoFeed(selectedCategory);

  // Video interaction hooks
  const videoInteractionManager = useVideoInteractionManager(user?.id || "");

  // Upload hooks
  const uploadVideoFile = useUploadVideoFile();
  const createVideoPost = useCreateVideoPost();

  // Get videos from feed
  const allVideos = feedData?.pages?.flatMap((page) => page.documents) ?? [];

  const filtered = allVideos.filter(
    (video) =>
      video.isActivated &&
      video.individual === "bizz" &&
      typeof video.videoUrl === "string" &&
      typeof video.title === "string"
  );

  // Force cast only after filtering and minimal shape validation
  const videos = filtered as unknown as VideoPost[];

  // Infinite scroll observer
  const lastVideoRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // Video intersection observer for auto-play and view tracking
  const videoIntersectionObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    videoIntersectionObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          const videoIndex = parseInt(video.dataset.index || "0");
          const videoId = video.dataset.videoId || "";

          if (entry.isIntersecting) {
            // Auto-play video when in view
            setCurrentVideoIndex(videoIndex);
            video.play();
            setIsPlaying(true);

            // Track video view
            if (user?.id && videoId) {
              videoInteractionManager.handleView(videoId);
            }
          } else {
            // Pause video when out of view
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    return () => {
      if (videoIntersectionObserver.current) {
        videoIntersectionObserver.current.disconnect();
      }
    };
  }, [user?.id, videoInteractionManager]);

  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("video/")) {
      toast({
        title: "Invalid File",
        description: "Please select a video file",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    const previewUrl = URL.createObjectURL(file);
    setFilePreviewUrl(previewUrl);
    setShowUploadModal(true);

    // Set form values
    form.setValue("file", [file]);

    // Get video duration
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      setVideoDuration(Math.floor(video.duration));
      URL.revokeObjectURL(video.src);
    };
    video.src = previewUrl;
  };

  const clearFileUpload = () => {
    setSelectedFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl("");
    }
    setVideoDuration(0);
    form.setValue("file", []);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Start camera for recording
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 720 },
          height: { ideal: 1280 },
        },
        audio: true,
      });

      streamRef.current = stream;
      if (recordingVideoRef.current) {
        recordingVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  // Start recording
  const startRecording = () => {
    if (!streamRef.current) return;

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : "video/webm",
      });

      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stopCamera();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 60) {
            // Max 60 seconds
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast({
        title: "Recording Error",
        description: "Failed to start recording. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Flip camera
  const flipCamera = async () => {
    stopCamera();
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    await startCamera();
  };

  // Reset recording
  const resetRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl("");
    setRecordingTime(0);
    startCamera();
  };

  // Hybrid upload handler using existing createPost pattern
  const handleHybridUpload = async () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to upload videos",
        variant: "destructive",
      });
      return;
    }

    const videoSource = recordedBlob || selectedFile;
    const title = form.getValues("title") || uploadTitle;
    const description = form.getValues("description") || uploadDescription;
    const category = form.getValues("category") || uploadCategory;

    if (!videoSource) {
      toast({
        title: "Missing Video",
        description: "Please record a video or select a file to upload",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Missing Title",
        description: "Please add a title for your video",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStage("Preparing upload...");

    let uploadedFiles: any[] = [];
    let fileUrls: string[] = [];

    try {
      // Create file from blob or use selected file
      let videoFile: File;
      if (recordedBlob) {
        videoFile = new File([recordedBlob], `video_${Date.now()}.webm`, {
          type: "video/webm",
        });
      } else {
        videoFile = selectedFile!;
      }

      const fileSizeMB = videoFile.size / (1024 * 1024);
      console.log(`Starting upload: ${fileSizeMB.toFixed(1)}MB file`);

      setUploadProgress(5);
      setUploadStage("Uploading video...");

      // Upload file using existing uploadFile function (like in createPost)
      const uploadedFile = await uploadFile(videoFile);
      if (!uploadedFile) {
        throw new Error(`Failed to upload video file`);
      }

      // Get file URL for video (use getFileView for videos)
      const fileUrl = getFileView(uploadedFile.$id);
      if (!fileUrl) {
        await deleteFile(uploadedFile.$id);
        throw new Error(`Failed to get URL for video file`);
      }

      uploadedFiles.push(uploadedFile);
      fileUrls.push(fileUrl);

      setUploadProgress(80);
      setUploadStage("Generating thumbnail...");

      // Generate thumbnail
      let thumbnailUrl = "";
      try {
        thumbnailUrl = await generateThumbnail(videoFile);
      } catch (error) {
        console.warn("Thumbnail generation failed:", error);
      }

      setUploadProgress(90);
      setUploadStage("Getting video metadata...");

      // Get video duration
      let duration = 0;
      try {
        const metadata = await getVideoMetadata(videoFile);
        duration = metadata.duration;
      } catch (error) {
        console.warn("Failed to get video metadata:", error);
      }

      setUploadProgress(95);
      setUploadStage("Creating post...");

      // Create video post document (similar to createPost structure)
      const newVideoPost = await createVideoPost.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        category: category.toLowerCase().trim(),
        videoUrl: fileUrl,
        thumbnailUrl,
        duration: duration,
        creatorId: user.id,
        individual: "bizz",
        isActivated: true,
      });

      if (!newVideoPost) {
        // Clean up uploaded files if post creation fails
        for (const uploadedFile of uploadedFiles) {
          try {
            await deleteFile(uploadedFile.$id);
          } catch (deleteError) {
            console.error(
              `Error deleting file ${uploadedFile.$id}:`,
              deleteError
            );
          }
        }
        throw new Error("Failed to create video post");
      }

      setUploadProgress(100);
      setUploadStage("Upload complete!");

      toast({
        title: "Video Uploaded Successfully!",
        description: `Your ${fileSizeMB.toFixed(1)}MB video has been uploaded and processed.`,
      });

      // Reset form and state
      form.reset();
      if (recordedBlob) {
        resetRecording();
      } else {
        clearFileUpload();
      }
      setUploadTitle("");
      setUploadDescription("");
      setUploadCategory("business");
      setShowRecordModal(false);
      setShowUploadModal(false);
      setUploadProgress(0);
      setUploadStage("");
    } catch (error) {
      console.error("Hybrid upload failed:", error);
      setUploadStage("Upload failed");

      // Clean up on error
      for (const uploadedFile of uploadedFiles) {
        try {
          await deleteFile(uploadedFile.$id);
        } catch (deleteError) {
          console.error(
            `Error deleting file ${uploadedFile.$id}:`,
            deleteError
          );
        }
      }

      toast({
        title: "Upload Failed",
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle video interactions with real APIs
  const handleVideoLike = async (videoId: string) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to like videos",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (!currentVideo) return;

      const currentState = videoInteractionStates[videoId];
      const isCurrentlyLiked = currentState?.isLiked ?? currentVideo.isLiked;
      const currentLikes =
        currentState?.tempLikes ?? (currentVideo.stats?.likes || 0);

      // Optimistic update - immediately update UI
      setVideoInteractionStates((prev) => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          isLiked: !isCurrentlyLiked,
          tempLikes: !isCurrentlyLiked
            ? currentLikes + 1
            : Math.max(0, currentLikes - 1),
          isSaved: currentState?.isSaved ?? currentVideo.isSaved,
          tempSaves:
            currentState?.tempSaves ?? (currentVideo.stats?.saves || 0),
        },
      }));
      await videoInteractionManager.handleLike(videoId);

      // Force refresh the specific video's stats
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
      });

      // Also refresh the feed to get updated interaction states
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
    } catch (error) {
      console.error("Error liking video:", error);
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (currentVideo) {
        setVideoInteractionStates((prev) => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            isLiked: currentVideo.isLiked,
            tempLikes: currentVideo.stats?.likes || 0,
          },
        }));
      }

      toast({
        title: "Error",
        description: "Failed to like video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVideoSave = async (videoId: string) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to save videos",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (!currentVideo) return;

      const currentState = videoInteractionStates[videoId];
      const isCurrentlySaved = currentState?.isSaved ?? currentVideo.isSaved;
      const currentSaves =
        currentState?.tempSaves ?? (currentVideo.stats?.saves || 0);

      // Optimistic update - immediately update UI
      setVideoInteractionStates((prev) => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          isSaved: !isCurrentlySaved,
          tempSaves: !isCurrentlySaved
            ? currentSaves + 1
            : Math.max(0, currentSaves - 1),
          isLiked: currentState?.isLiked ?? currentVideo.isLiked,
          tempLikes:
            currentState?.tempLikes ?? (currentVideo.stats?.likes || 0),
        },
      }));
      await videoInteractionManager.handleSave(videoId);

      // Force refresh the specific video's stats
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
      });

      // Also refresh the feed to get updated interaction states
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
    } catch (error) {
      console.error("Error saving video:", error);

      const currentVideo = videos.find((v) => v.$id === videoId);
      if (currentVideo) {
        setVideoInteractionStates((prev) => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            isSaved: currentVideo.isSaved,
            tempSaves: currentVideo.stats?.saves || 0,
          },
        }));
      }

      toast({
        title: "Error",
        description: "Failed to save video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVideoShare = async (videoId: string) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Check out this business video",
          url: `${window.location.origin}/bizz/video/${videoId}`,
        });
      } else {
        await navigator.clipboard.writeText(
          `${window.location.origin}/bizz/video/${videoId}`
        );
        toast({
          title: "Link Copied!",
          description: "Video link copied to clipboard",
        });
      }

      // Track share interaction
      if (user?.id) {
        await videoInteractionManager.handleShare(videoId);

        // Force refresh the specific video's stats
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
        });

        // Also refresh the feed to get updated interaction states
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
        });
      }
    } catch (error) {
      console.error("Error sharing video:", error);
    }
  };

  const handleTipClick = (video: VideoPost) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to send tips",
        variant: "destructive",
      });
      return;
    }
    setSelectedVideo(video);
    setShowTipModal(true);
  };

  const handleSendTip = async () => {
    if (!selectedVideo || !user?.id) return;

    try {
      await handleTipPayment(
        user.id, // senderId
        selectedVideo.creator.$id, // receiverId
        selectedVideo.$id, // videoId
        tipAmount, // amount
        {
          name: user.name,
          emailOrPhone: user.emailOrPhone,
        }, // sender info
        () => {
          // onSuccess callback
          setShowTipModal(false);
          setSelectedVideo(null);
          setTipAmount(10);
          setTipMessage("");

          // Refresh video stats to show updated tip count
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_VIDEO_STATS, selectedVideo.$id],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
          });
        }
      );
    } catch (error) {
      console.error("Error in tip payment:", error);
      // Error handling is already done in useVideoTipPayment hook
    }
  };

  // Video playback controls
  const togglePlayPause = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // Initialize camera when modal opens
  useEffect(() => {
    if (showRecordModal && !recordedBlob) {
      startCamera();
    }

    return () => {
      if (showRecordModal) {
        stopCamera();
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }
      }
    };
  }, [showRecordModal]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      stopCamera();
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (videoIntersectionObserver.current) {
        videoIntersectionObserver.current.disconnect();
      }
    };
  }, []);

  // Set up video intersection observers when videos change
  useEffect(() => {
    if (videoIntersectionObserver.current) {
      videoRefs.current.forEach((video) => {
        if (video) {
          videoIntersectionObserver.current?.observe(video);
        }
      });
    }

    return () => {
      if (videoIntersectionObserver.current) {
        videoRefs.current.forEach((video) => {
          if (video) {
            videoIntersectionObserver.current?.unobserve(video);
          }
        });
      }
    };
  }, [videos.length]);

  if (isError) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something went wrong</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOComponent
        title="BUSICONN - Bizz"
        description="Short business videos, skill tutorials, and professional content. Share your business knowledge and skills."
        canonicalUrl="https://busiconn.com/bizz"
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Main Video Feed */}
      <div className="h-screen w-full relative overflow-hidden bg-black">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2">
            <h1 className="text-white font-bold text-xl">Bizz</h1>
            <Button
              onClick={() => setShowGuidelinesModal(true)}
              className="text-white/70 hover:text-white p-1"
              variant="ghost">
              <Info className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2">
              <Upload className="h-5 w-5" />
            </Button>
            <Button
              onClick={() => setShowRecordModal(true)}
              className="bg-primary-500 hover:bg-primary-600 text-white rounded-full p-2">
              <Plus className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Video Container */}
        <div
          ref={containerRef}
          className="h-full w-full relative snap-y snap-mandatory overflow-y-scroll">
          {videos.map((video, index) => {
            const interactionState = videoInteractionStates[video.$id];
            const isLiked = interactionState?.isLiked ?? video.isLiked;
            const isSaved = interactionState?.isSaved ?? video.isSaved;

            // Use optimistic stats if available
            const stats = {
              likes: interactionState?.tempLikes ?? (video.stats?.likes || 0),
              shares: video.stats?.shares || 0,
              saves: interactionState?.tempSaves ?? (video.stats?.saves || 0),
              tips: video.stats?.tips || 0,
              views: video.stats?.views || 0,
            };
            return (
              <div
                key={video.$id}
                ref={index === videos.length - 1 ? lastVideoRef : null}
                className="h-full w-full relative snap-start flex items-center justify-center bg-black">
                {/* Desktop/Laptop Video Container with proper aspect ratio */}
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* Video with responsive sizing */}
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                      if (el) {
                        el.dataset.index = index.toString();
                        el.dataset.videoId = video.$id;
                      }
                    }}
                    src={video.videoUrl}
                    className="
              h-full w-full object-cover
              md:h-full md:w-auto md:max-w-[400px] md:object-contain
              lg:h-full lg:w-auto lg:max-w-[450px] lg:object-contain
              xl:h-full xl:w-auto xl:max-w-[500px] xl:object-contain
            "
                    loop
                    muted={isMuted}
                    playsInline
                    onClick={() => togglePlayPause(index)}
                  />

                  {/* Black bars/background for desktop to maintain mobile-first feel */}
                  <div className="hidden md:block absolute inset-0 bg-black -z-10"></div>
                </div>

                {/* Video Overlay Controls */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {!isPlaying && currentVideoIndex === index && (
                    <Button
                      onClick={() => togglePlayPause(index)}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-4">
                      <Play className="h-8 w-8" />
                    </Button>
                  )}
                </div>

                {/* Creator Info - Responsive positioning */}
                <div className="absolute bottom-16 md:bottom-20 left-4 right-24 md:right-28 lg:right-32 xl:right-36 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={video.creator.imageUrl}
                      alt={video.creator.name}
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-white"
                    />
                    <div>
                      <p className="font-semibold text-sm md:text-base">
                        {video.creator.name}
                      </p>
                      <p className="text-xs md:text-sm text-gray-300">
                        @{video.creator.username}
                      </p>
                    </div>
                  </div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-sm text-gray-200 line-clamp-2 md:line-clamp-3">
                    {video.description}
                  </p>

                  {/* Category Badge */}
                  <div className="mt-2">
                    {(() => {
                      const categoryInfo = getCategoryInfo(video.category);
                      const IconComponent = categoryInfo.icon;
                      return (
                        <div className="inline-flex items-center gap-1 bg-black/60 px-2 py-1 rounded-full">
                          <IconComponent
                            className={`h-3 w-3 ${categoryInfo.colorClass}`}
                          />
                          <span className="text-xs text-gray-300">
                            {categoryInfo.label}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Action Buttons - ENHANCED WITH BETTER VISUAL STATES */}
                <div className="absolute right-2 md:right-4 lg:right-6 xl:right-8 top-1/2 transform -translate-y-1/2 flex flex-col gap-3 z-10">
                  {/* Tip - Most prominent at top */}
                  <div className="flex flex-col items-center">
                    <Button
                      onClick={() => handleTipClick(video)}
                      className="bg-green-500 hover:bg-green-600 text-white rounded-full p-3 shadow-lg border-2 border-white/20">
                      <IndianRupee className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                    <span className="text-white text-xs mt-1 font-medium bg-black/50 px-2 py-0.5 rounded-full">
                      {stats.tips}
                    </span>
                  </div>

                  {/* Like - PROFESSIONAL */}
                  <div className="flex flex-col items-center">
                    <Button
                      onClick={() => handleVideoLike(video.$id)}
                      disabled={videoInteractionManager.isLiking}
                      className={`rounded-full p-2.5 md:p-3 shadow-lg ${
                        isLiked
                          ? "bg-rose-500 hover:bg-rose-600"
                          : "bg-black/50 hover:bg-black/70"
                      } text-white`}>
                      <Heart
                        className={`h-5 w-5 md:h-6 md:w-6 ${isLiked ? "fill-white" : ""}`}
                      />
                    </Button>
                    <span
                      className={`text-white text-xs mt-1 font-medium px-2 py-0.5 rounded-full ${
                        isLiked ? "bg-rose-500/50" : "bg-black/50"
                      }`}>
                      {stats.likes > 999
                        ? `${(stats.likes / 1000).toFixed(1)}k`
                        : stats.likes}
                    </span>
                  </div>

                  {/* Save - PROFESSIONAL */}
                  <div className="flex flex-col items-center">
                    <Button
                      onClick={() => handleVideoSave(video.$id)}
                      disabled={videoInteractionManager.isSaving}
                      className={`rounded-full p-2.5 md:p-3 shadow-lg ${
                        isSaved
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-black/50 hover:bg-black/70"
                      } text-white`}>
                      {isSaved ? (
                        <BookmarkCheck className="h-5 w-5 md:h-6 md:w-6" />
                      ) : (
                        <Bookmark className="h-5 w-5 md:h-6 md:w-6" />
                      )}
                    </Button>
                    <span
                      className={`text-white text-xs mt-1 font-medium px-2 py-0.5 rounded-full ${
                        isSaved ? "bg-blue-500/50" : "bg-black/50"
                      }`}>
                      {stats.saves}
                    </span>
                  </div>

                  {/* Share */}
                  <div className="flex flex-col items-center">
                    <Button
                      onClick={() => handleVideoShare(video.$id)}
                      disabled={videoInteractionManager.isSharing}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 md:p-3 shadow-lg">
                      <Share className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                    <span className="text-white text-xs mt-1 font-medium bg-black/50 px-2 py-0.5 rounded-full">
                      {stats.shares}
                    </span>
                  </div>

                  {/* Mute Toggle */}
                  <Button
                    onClick={() => toggleMute(index)}
                    className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 md:p-3 shadow-lg">
                    {isMuted ? (
                      <VolumeX className="h-4 w-4 md:h-5 md:w-5" />
                    ) : (
                      <Volume2 className="h-4 w-4 md:h-5 md:w-5" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Loading More Indicator */}
          {isFetchingNextPage && (
            <div className="h-screen w-full flex items-center justify-center bg-black">
              <Loader />
            </div>
          )}
        </div>

        {/* Record Modal */}
        {showRecordModal && (
          <div className="fixed inset-0 bg-black z-50">
            <div className="h-full w-full relative">
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
                <Button
                  onClick={() => {
                    setShowRecordModal(false);
                    resetRecording();
                    stopCamera();
                  }}
                  className="text-white bg-black/50 hover:bg-black/70 rounded-full p-2">
                  <X className="h-6 w-6" />
                </Button>

                {isRecording && (
                  <div className="bg-rose-500 px-3 py-1 rounded-full">
                    <span className="text-white font-mono text-sm">
                      {Math.floor(recordingTime / 60)}:
                      {(recordingTime % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                )}

                <Button
                  onClick={flipCamera}
                  className="text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
                  disabled={isRecording}>
                  <RotateCcw className="h-6 w-6" />
                </Button>
              </div>

              {/* Video Preview */}
              <div className="h-full w-full flex items-center justify-center">
                {recordedBlob ? (
                  <video
                    src={recordedUrl}
                    controls
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <video
                    ref={recordingVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                )}
              </div>

              {/* Controls */}
              <div className="absolute bottom-20 left-0 right-0 flex justify-center">
                {!recordedBlob ? (
                  <Button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`rounded-full p-6 ${
                      isRecording
                        ? "bg-rose-500 hover:bg-rose-600"
                        : "bg-white hover:bg-gray-200"
                    }`}
                    disabled={!streamRef.current}>
                    {isRecording ? (
                      <StopCircle className="h-8 w-8 text-white" />
                    ) : (
                      <Camera className="h-8 w-8 text-black" />
                    )}
                  </Button>
                ) : (
                  <div className="flex gap-4">
                    <Button
                      onClick={resetRecording}
                      className="bg-gray-500 hover:bg-gray-600 text-white rounded-full px-6 py-3">
                      Retake
                    </Button>
                    <Button
                      onClick={() => setShowRecordModal(false)}
                      className="bg-primary-500 hover:bg-primary-600 text-white rounded-full px-6 py-3">
                      Next
                    </Button>
                  </div>
                )}
              </div>

              {/* Guidelines */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/70 rounded-lg p-3">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-xs font-medium mb-1">
                        Business Content Only
                      </p>
                      <p className="text-gray-300 text-xs">
                        Share business tips, demonstrate skills, showcase
                        products/services, or teach valuable techniques. Choose
                        the right category for better discovery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Modal with Form */}
        {showUploadModal && selectedFile && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 pb-20">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Upload Business Video
                </h2>
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    clearFileUpload();
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* Video Preview */}
                <video
                  ref={previewVideoRef}
                  src={filePreviewUrl}
                  controls
                  className="w-full rounded-lg bg-black"
                  style={{ maxHeight: "200px" }}
                />

                {/* File Info */}
                <div className="bg-dark-3 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Video className="h-4 w-4" />
                    <span>{selectedFile.name}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-400">
                    <span>
                      Size: {(selectedFile.size / (1024 * 1024)).toFixed(1)}MB
                    </span>
                    {videoDuration > 0 && (
                      <span>
                        Duration: {Math.floor(videoDuration / 60)}:
                        {(videoDuration % 60).toString().padStart(2, "0")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Enhanced Form */}
                <Form {...form}>
                  <form className="space-y-4">
                    {/* Category Selection - FIXED DROPDOWN */}
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Business Category *
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <select
                                {...field}
                                className="w-full bg-dark-3 border border-dark-4 text-white rounded-lg p-3 pr-8 appearance-none cursor-pointer">
                                {ENHANCED_BUSINESS_CATEGORIES.map(
                                  (category) => (
                                    <option
                                      key={category.value}
                                      value={category.value}>
                                      {category.label}
                                    </option>
                                  )
                                )}
                              </select>
                              {/* Custom dropdown arrow */}
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </FormControl>
                          {field.value && (
                            <div className="bg-dark-3 rounded-lg p-2 mt-2">
                              {(() => {
                                const categoryInfo = getCategoryInfo(
                                  field.value
                                );
                                const IconComponent = categoryInfo.icon;
                                return (
                                  <div className="flex items-start gap-2">
                                    <IconComponent
                                      className={`h-4 w-4 ${categoryInfo.colorClass} mt-0.5 flex-shrink-0`}
                                    />
                                    <div>
                                      <p className="text-xs font-medium text-white">
                                        {categoryInfo.label}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {categoryInfo.description}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Examples: {categoryInfo.examples}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Title *
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "title"
                              )}
                              className="bg-dark-3 border-dark-4 text-white"
                              maxLength={100}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/100
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Description
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "description"
                              )}
                              className="bg-dark-3 border-dark-4 text-white min-h-[80px]"
                              maxLength={500}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/500
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    {/* REMOVE THE FileUploader COMPONENT - WE DON'T NEED IT */}
                    {/* The file is already selected via the hidden input */}
                  </form>
                </Form>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{uploadStage}</span>
                      <span className="text-white">
                        {Math.floor(uploadProgress)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.floor(uploadProgress)}%`,
                        }}></div>
                    </div>
                    {uploadStage && (
                      <div className="text-xs text-gray-400">{uploadStage}</div>
                    )}
                  </div>
                )}

                {/* Upload Button */}
                <Button
                  onClick={handleHybridUpload}
                  disabled={
                    !form.watch("title")?.trim() ||
                    isUploading ||
                    uploadVideoFile.status === "loading" ||
                    createVideoPost.status === "loading"
                  }
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white">
                  {isUploading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      Uploading Business Video...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Business Video
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Post Details Modal (for recorded videos) */}
        {!showRecordModal && recordedBlob && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Post Business Video
                </h2>
                <Button
                  onClick={() => {
                    resetRecording();
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* Video Preview */}
                <video
                  src={recordedUrl}
                  controls
                  className="w-full rounded-lg bg-black"
                  style={{ maxHeight: "200px" }}
                />

                {/* Enhanced Form for recorded video */}
                <Form {...form}>
                  <form className="space-y-4">
                    {/* FIXED Category Selection */}
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Business Category *
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <select
                                {...field}
                                className="w-full bg-dark-3 border border-dark-4 text-white rounded-lg p-3 pr-8 appearance-none cursor-pointer">
                                {ENHANCED_BUSINESS_CATEGORIES.map(
                                  (category) => (
                                    <option
                                      key={category.value}
                                      value={category.value}>
                                      {category.label}
                                    </option>
                                  )
                                )}
                              </select>
                              {/* Custom dropdown arrow */}
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </FormControl>
                          {field.value && (
                            <div className="bg-dark-3 rounded-lg p-2 mt-2">
                              {(() => {
                                const categoryInfo = getCategoryInfo(
                                  field.value
                                );
                                const IconComponent = categoryInfo.icon;
                                return (
                                  <div className="flex items-start gap-2">
                                    <IconComponent
                                      className={`h-4 w-4 ${categoryInfo.colorClass} mt-0.5 flex-shrink-0`}
                                    />
                                    <div>
                                      <p className="text-xs font-medium text-white">
                                        {categoryInfo.label}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {categoryInfo.description}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Examples: {categoryInfo.examples}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Title *
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "title"
                              )}
                              className="bg-dark-3 border-dark-4 text-white"
                              maxLength={100}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/100
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Description
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "description"
                              )}
                              className="bg-dark-3 border-dark-4 text-white min-h-[80px]"
                              maxLength={500}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/500
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{uploadStage}</span>
                      <span className="text-white">
                        {Math.floor(uploadProgress)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.floor(uploadProgress)}%`,
                        }}></div>
                    </div>
                    {uploadStage && (
                      <div className="text-xs text-gray-400">{uploadStage}</div>
                    )}
                  </div>
                )}

                {/* Post Button */}
                <Button
                  onClick={handleHybridUpload}
                  disabled={
                    !form.watch("title")?.trim() ||
                    isUploading ||
                    uploadVideoFile.status === "loading" ||
                    createVideoPost.status === "loading"
                  }
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white">
                  {isUploading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      Posting Business Video...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Post Business Video
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tip Modal */}
        {showTipModal && selectedVideo && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-sm">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-lg font-bold text-white">Send Tip</h2>
                <Button
                  onClick={() => {
                    setShowTipModal(false);
                    setSelectedVideo(null);
                    setTipAmount(10);
                    setTipMessage("");
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                <div className="text-center">
                  <img
                    src={selectedVideo.creator.imageUrl}
                    alt={selectedVideo.creator.name}
                    className="w-16 h-16 rounded-full mx-auto mb-2 object-cover"
                  />
                  <p className="text-white font-medium">
                    {selectedVideo.creator.name}
                  </p>
                  <p className="text-gray-400 text-sm">
                    @{selectedVideo.creator.username}
                  </p>
                  <p className="text-gray-300 text-xs mt-1 line-clamp-1">
                    {selectedVideo.title}
                  </p>
                </div>

                {/* Tip Amount Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Choose Amount
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[10, 25, 50, 100, 250, 500].map((amount) => (
                      <Button
                        key={amount}
                        onClick={() => setTipAmount(amount)}
                        className={`${
                          tipAmount === amount
                            ? "bg-primary-500 text-white"
                            : "bg-dark-3 text-gray-300 hover:bg-dark-4"
                        } rounded-lg py-2 text-sm font-medium`}>
                        ₹{amount}
                      </Button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <Input
                    type="number"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(Number(e.target.value))}
                    placeholder="Custom amount"
                    className="bg-dark-3 border-dark-4 text-white"
                    min="1"
                    max="10000"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Min: ₹1, Max: ₹10,000
                  </p>
                </div>

                {/* Optional Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message (Optional)
                  </label>
                  <Input
                    value={tipMessage}
                    onChange={(e) => setTipMessage(e.target.value)}
                    placeholder="Add a nice message..."
                    className="bg-dark-3 border-dark-4 text-white"
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {tipMessage.length}/100
                  </p>
                </div>

                {/* Payment Info */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <p className="text-green-200 text-sm">
                      Secure payment via Cashfree Gateway
                    </p>
                  </div>
                </div>

                {/* Send Tip Button */}
                <Button
                  onClick={handleSendTip}
                  disabled={tipAmount < 1 || isPaymentLoading}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3">
                  {isPaymentLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      Processing Payment...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <IndianRupee className="h-4 w-4" />
                      Send ₹{tipAmount} Tip
                    </div>
                  )}
                </Button>

                {/* Terms */}
                <p className="text-xs text-gray-400 text-center">
                  By sending a tip, you agree to our terms and privacy policy
                </p>
              </div>
            </div>
          </div>
        )}

        {showGuidelinesModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Business Content Guidelines
                </h2>
                <Button
                  onClick={() => setShowGuidelinesModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* What's Allowed */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <h3 className="text-lg font-semibold text-white">
                      What Belongs on Bizz
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {CONTENT_GUIDELINES.allowed.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-sm text-gray-300">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What's Not Allowed */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-rose-500" />
                    <h3 className="text-lg font-semibold text-white">
                      What Doesn't Belong
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {CONTENT_GUIDELINES.notAllowed.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-sm text-gray-300">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Categories Preview */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Available Categories
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {ENHANCED_BUSINESS_CATEGORIES.slice(0, 6).map(
                      (category) => {
                        const IconComponent = category.icon;
                        return (
                          <div
                            key={category.value}
                            className="flex items-center gap-3 p-2 bg-dark-3 rounded-lg">
                            <IconComponent
                              className={`h-4 w-4 ${category.colorClass}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-white">
                                {category.label}
                              </p>
                              <p className="text-xs text-gray-400">
                                {category.description}
                              </p>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                  {ENHANCED_BUSINESS_CATEGORIES.length > 6 && (
                    <p className="text-xs text-gray-400 mt-2">
                      ...and {ENHANCED_BUSINESS_CATEGORIES.length - 6} more
                      categories
                    </p>
                  )}
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-blue-200 text-sm">
                    <strong>Remember:</strong> Bizz is for business
                    professionals. Keep content valuable, educational, and
                    business-focused.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Loading State for Video Feed */}
        {isLoading && !feedData && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center">
              <Loader />
              <p className="text-white text-sm">Loading videos...</p>
            </div>
          </div>
        )}

        {/* No Videos State */}
        {!isLoading && videos.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center px-8">
              <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">
                No Business Videos Yet
              </h3>
              <p className="text-gray-400 text-sm mb-2">
                Be the first to share your business knowledge!
              </p>
              <p className="text-gray-500 text-xs mb-6">
                Share tutorials, showcase services, demonstrate skills, or tell
                your business story
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3">
                  <Upload className="h-5 w-5 mr-2" />
                  Upload Video
                </Button>
                <Button
                  onClick={() => setShowRecordModal(true)}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3">
                  <Plus className="h-5 w-5 mr-2" />
                  Record Video
                </Button>
              </div>
              <Button
                onClick={() => setShowGuidelinesModal(true)}
                className="mt-4 text-gray-400 hover:text-white text-sm"
                variant="ghost">
                <Info className="h-4 w-4 mr-1" />
                View Content Guidelines
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Bizz;
